import { useEffect, useState } from 'react'
import { flushSync } from 'react-dom'
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form'
import {
  Box as MuiBox,
  formControlClasses,
  formGroupClasses,
  FormLabel,
  SxProps,
} from '@mui/material'

import { DefaultElementProps, Element } from 'common/interfaces'
import { fieldsetBorder } from 'common/styles'
import DeleteAppendedItemModal from 'components/Modals/DeleteAppendedItemModal'
import { Box } from 'elements/Box'
import { useBoolean } from 'utils/hook'
import { concatToSXProp } from 'utils/style'

import AddAnotherButton from './components/AddAnotherButton'
import CollapseButton from './components/ColapseButton'
import RemoveButton from './components/RemoveButton'
import { Append as AppendProps } from './interface'

/**
 * Applies the correct dynamic prefix to render rule keys.
 * Handles both legacy format with embedded index (e.g. `session.passengerAppend.[0].element`)
 * and new simplified format (e.g. `session.element`).
 */
function applyPrefixToRenderRules(rules: Record<string, any>, prefix: string): Record<string, any> {
  const newRule: Record<string, any> = {}

  for (const [key, value] of Object.entries(rules)) {
    // Remove the leading "session." prefix
    let path = key.replace(/^session\./, '')

    // Remove legacy embedded array index if present
    // Example: passengerAppend.[0].element → element
    if (/\[\d+\]/.test(path)) {
      const parts = path.split('.')
      path = parts.slice(parts.findIndex((p) => /\[\d+\]/.test(p)) + 1).join('.')
    }

    // Construct the new key with the actual prefix
    const fullKey = `session.${prefix}.${path}`

    newRule[fullKey] = value
  }

  return newRule
}

function recursivelyAddPrefixToElements(elements: Element[], prefix: string): Element[] {
  if (elements === undefined) {
    return []
  }

  return elements.map((element) => {
    const updatedRenderRules =
      element.render_rules?.map((rule) => ({
        ...rule,
        rules: applyPrefixToRenderRules(rule.rules, prefix),
      })) ?? []

    const prefixedElement: Element = {
      ...element,
      name: `${prefix}.${element.name}`,
      render_rules: updatedRenderRules,
    }

    if (element.type !== Append.name) {
      prefixedElement.children = recursivelyAddPrefixToElements(element.children, prefix)
    }

    return prefixedElement
  })
}

const styles: Record<string, SxProps> = {
  box: {
    position: 'relative',
    width: '100%',
    ':not(:last-child)': {
      borderBottom: '1px solid #0000001F',
      marginBottom: '24px',
      paddingBottom: '24px',
    },
    [`& .${formGroupClasses.root} > div`]: {
      flex: 1,
      '& > .sizing-container': {
        width: '100%',
      },
    },
  },

  formLabel: {
    display: 'inline-block',
    mb: '12px',
  },
}

export default function Append({
  name,
  label,
  border = true,
  direction = 'column',
  children,
  deletingWord = 'item',
  background,
}: AppendProps & DefaultElementProps): JSX.Element | null {
  const [isModalOpen, openModal, closeModal] = useBoolean(false)

  const [selectedIndex, setSelectedIndex] = useState(0)
  const [collapsed, setCollapsed] = useState<boolean[]>([])

  const { control, setValue, getFieldState, formState } = useFormContext()
  const { fields, remove, append, replace } = useFieldArray({
    control,
    name,
  })

  const openDeleteModalForOneItem = (index: number): void => {
    openModal()
    setSelectedIndex(index)
  }

  const deleteSelectedItem = (): void => {
    closeModal()
    remove(selectedIndex)
  }

  // this effect is stands for having at least
  // one group of children after mounting
  useEffect(() => {
    if (fields.length === 0) {
      replace([{}])
    }
  }, [replace])

  const toggleCollapse = (index: number): void => {
    setCollapsed((prev) => {
      const next = [...prev]
      next[index] = !next[index]
      return next
    })
  }

  if (children === undefined) {
    return null
  }

  const fieldsCount = fields.length
  const mappedChildren = fields.map((item, index) => {
    const fieldNamesForIndex = Object.keys(item)
      .filter((fieldKey) => fieldKey !== 'id')
      .map((fieldKey) => `${name}.${index}.${fieldKey}`)

    const hasErrors = fieldNamesForIndex.some((n) => getFieldState(n, formState).invalid)

    const elements = recursivelyAddPrefixToElements(children, `${name}.${index}`)

    return (
      <MuiBox
        className="append-input-wrap"
        onPointerDownCapture={() => {
          flushSync(() => setSelectedIndex(index))
          setValue('currentIndex', index)
        }}
        sx={concatToSXProp(styles.box, {
          ...(!label && {
            '&:first-child': {
              paddingTop: '24px',
              borderTop: '1px solid #0000001F',
            },
          }),
        })}
        key={item.id}
      >
        {label !== undefined && (
          <MuiBox
            sx={{
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <FormLabel
              sx={{
                ...(border ? {} : styles.formLabel),
                ...(collapsed[index] && hasErrors
                  ? { color: (theme) => `${theme.palette.error.main} !important` }
                  : {}),
              }}
              error={true}
            >
              {label}
              {fieldsCount > 1 && ` #${index + 1}`}
            </FormLabel>
            <CollapseButton
              onClick={() => toggleCollapse(index)}
              collapsed={collapsed[index]}
              sx={{ ml: 1 }}
            />
          </MuiBox>
        )}
        <MuiBox
          aria-hidden={collapsed[index] ? true : undefined}
          sx={{
            display: collapsed[index] ? 'none' : 'flex',
            alignItems: 'start',
            background: background,
            pointerEvents: collapsed[index] ? 'none' : 'auto',
            ...(border && {
              ...fieldsetBorder,
              [`& > .${formControlClasses.root}`]: { border: 'none' },
            }),
          }}
        >
          <Box
            key={item.id}
            direction={direction}
            border={border}
            validations={{}}
            name={`${name}-box-${item.id}`}
          >
            {elements}
          </Box>
          <RemoveButton
            onClick={() => openDeleteModalForOneItem(index)}
            border={border}
            sx={{
              right: border ? '-17px' : '-8px',
              top: label !== undefined ? '-6px' : '-17px',
            }}
          />
        </MuiBox>
      </MuiBox>
    )
  })

  return (
    <MuiBox className="append-wrap">
      {mappedChildren}
      <AddAnotherButton onClick={() => append({})} text={deletingWord} />
      {isModalOpen && (
        <DeleteAppendedItemModal
          open={isModalOpen}
          onClose={closeModal}
          onDelete={deleteSelectedItem}
          title={deletingWord}
        />
      )}
    </MuiBox>
  )
}
